'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  analyticsApi,
  type AnomaliesParams,
  type ForecastParams,
  type RecommendationsParams,
  type GenerateRecommendationsParams,
  type RecommendationFeedbackBody,
  type SchemesParams,
  type GraphParams,
  type GraphClustersParams,
  type GraphFeaturesResponse,
} from './analytics.api'

// Параметры запросов определены рядом с ресурсом (analytics.api.ts) и
// реэкспортятся здесь — существующие импортёры `@/hooks/useAnalytics` работают.
export type {
  AnomaliesParams,
  ForecastParams,
  RecommendationsParams,
  GenerateRecommendationsParams,
  RecommendationFeedbackBody,
  SchemesParams,
  GraphParams,
  GraphFeaturesParams,
  GraphAnalyzeResponse,
  GraphAnalyzeNode,
  GraphCluster,
  GraphClustersListResponse,
  GraphClustersListItem,
  RecommendationItem,
  RecommendationsResponse,
  KpiResponse,
  KpiItem,
  AnomaliesResponse,
  AnomalyItem,
  ForecastResponse,
  ForecastPoint,
  ForecastTrend,
  CargoResponse,
  CargoRiskProfile,
  CargoSubstitution,
  CargoFlow,
  CargoRoute,
  FinancialResponse,
  FinancialMetric,
  FinancialRegion,
} from './analytics.api'

// Re-export локально импортированных типов (используются в этом модуле).
export type { GraphClustersParams, GraphFeaturesResponse }

const STALE_TIME = 5 * 60 * 1000

/**
 * Фабрика queryKey аналитики. Первый элемент каждого ключа сохранён из
 * прежней реализации — инвалидация мутаций остаётся совместимой.
 */
export const analyticsKeys = {
  anomalies: (params?: AnomaliesParams) => ['anomalies', params] as const,
  forecast: (params?: ForecastParams) => ['forecast', params] as const,
  cargo: (type: string) => ['cargo', type] as const,
  financial: (type: string) => ['financial', type] as const,
  kpis: () => ['kpis'] as const,
  recommendations: (params?: RecommendationsParams) => ['recommendations-analytics', params] as const,
  recommendationsAll: ['recommendations-analytics'] as const,
  schemes: (params?: SchemesParams) => ['schemes', params] as const,
  graph: (params?: GraphParams) => ['graph', params] as const,
  graphClusters: (params?: GraphClustersParams) => ['graph-clusters', params] as const,
  graphFeatures: (bin: string) => ['graph-features', bin] as const,
}

export function useAnomalies(params?: AnomaliesParams) {
  return useQuery({
    queryKey: analyticsKeys.anomalies(params),
    queryFn: ({ signal }) => analyticsApi.getAnomalies(params, { signal }),
    staleTime: STALE_TIME,
  })
}

export function useForecast(params?: ForecastParams) {
  return useQuery({
    queryKey: analyticsKeys.forecast(params),
    queryFn: ({ signal }) => analyticsApi.getForecast(params, { signal }),
    staleTime: STALE_TIME,
  })
}

export function useCargoIntelligence(type: string = 'risk') {
  return useQuery({
    queryKey: analyticsKeys.cargo(type),
    queryFn: ({ signal }) => analyticsApi.getCargo(type, { signal }),
    staleTime: STALE_TIME,
  })
}

export function useFinancialMetrics(type: string = 'risk') {
  return useQuery({
    queryKey: analyticsKeys.financial(type),
    queryFn: ({ signal }) => analyticsApi.getFinancial(type, { signal }),
    staleTime: STALE_TIME,
  })
}

export function useKpis() {
  return useQuery({
    queryKey: analyticsKeys.kpis(),
    queryFn: ({ signal }) => analyticsApi.getKpi({ signal }),
    staleTime: STALE_TIME,
  })
}

export function useRecommendationsAnalytics(params?: RecommendationsParams) {
  return useQuery({
    queryKey: analyticsKeys.recommendations(params),
    queryFn: ({ signal }) => analyticsApi.getRecommendations(params, { signal }),
    staleTime: STALE_TIME,
  })
}

export function useGenerateRecommendations() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params?: GenerateRecommendationsParams) =>
      analyticsApi.generateRecommendations(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.recommendationsAll })
    },
  })
}

export function useRecommendationFeedback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ recId, feedback }: RecommendationFeedbackBody) =>
      analyticsApi.recommendationFeedback({ recId, feedback }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.recommendationsAll })
    },
  })
}

export function useSchemes(params?: SchemesParams) {
  return useQuery({
    queryKey: analyticsKeys.schemes(params),
    queryFn: ({ signal }) => analyticsApi.getSchemes(params, { signal }),
    staleTime: STALE_TIME,
  })
}

export function useGraphData(params?: GraphParams, opts?: { staleTime?: number }) {
  return useQuery({
    queryKey: analyticsKeys.graph(params),
    queryFn: ({ signal }) => analyticsApi.getGraph(params, { signal }),
    staleTime: opts?.staleTime ?? STALE_TIME,
  })
}

export function useGraphClusters(params?: GraphClustersParams, opts?: { staleTime?: number }) {
  return useQuery({
    queryKey: analyticsKeys.graphClusters(params),
    queryFn: ({ signal }) => analyticsApi.getGraphClusters(params, { signal }),
    staleTime: opts?.staleTime ?? STALE_TIME,
  })
}

/**
 * Граф-фичи компании по БИН. Запрос включается только при валидном БИН
 * (enabled), что повторяет прежнее поведение страницы графовой аналитики.
 */
export function useGraphFeatures(
  bin: string,
  opts?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery<GraphFeaturesResponse>({
    queryKey: analyticsKeys.graphFeatures(bin),
    queryFn: ({ signal }) => analyticsApi.getGraphFeatures({ bin }, { signal }),
    enabled: opts?.enabled ?? bin.length === 12,
    staleTime: opts?.staleTime ?? STALE_TIME,
  })
}
